import { Repeat } from '@griply/assets';
import { CalendarTaskSync } from '@griply/types';
import {
  getScheduleDayTarget,
  getTaskDayCompletionPercentage,
} from '@griply/utils';
import React, { useCallback, useMemo } from 'react';
import { IconFormIcons } from 'shared/assets/icons';
import { TaskCardContextMenu } from 'shared/components/connected/task-card-context-menu';
import { Icon } from 'shared/components/ui/icon';
import { TaskDetailCheckbox } from 'shared/components/ui/task-detail-checkbox';
import { useToday } from 'shared/contexts/today';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { ID } from 'shared/types/id';
import { Task, TaskCheckOptions } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';

import * as Styled from './event.style';
import { EventCard } from './event-card';
import { TaskEventSyncBadge } from './task-event-sync-badge';

export type EventTaskProps = {
  task: Task;
  occurrenceId?: ID;
  onComplete: (id: ID, date: Timestamp) => void;
  referenceDate: string;
  weekStartsOn: WeekDays;
  onClick?: (id: ID) => void;
  isDeadline?: boolean;
  showDeadlineFlag?: boolean;
  color?: string;
  numberOfLines: number;
  timeText?: string;
  completed?: boolean;
  checkOption?: TaskCheckOptions;
  isFutureRepeating?: boolean;
  onInteractionComplete?: () => void;
  calendarSync?: CalendarTaskSync;
};

export const TaskBasicEvent: React.FC<EventTaskProps> = ({
  task,
  occurrenceId,
  referenceDate,
  onComplete,
  weekStartsOn,
  onClick,
  isDeadline,
  showDeadlineFlag,
  color,
  numberOfLines,
  timeText,
  completed,
  checkOption = TaskCheckOptions.Unchecked,
  isFutureRepeating,
  onInteractionComplete,
  calendarSync,
}) => {
  const today = useToday();
  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState();

  const refDate = useMemo(() => new Date(referenceDate), [referenceDate]);

  const _onClick = () => onClick?.(task.id);

  const _onComplete = useCallback(() => {
    if (isFutureRepeating) {
      // do not allow completing habits for future dates
      onClick?.(task.id);
      return;
    }
    onComplete(task.id, refDate);
  }, [isFutureRepeating, onClick, onComplete, refDate, task.id]);

  const completionPercentage = useMemo(
    () =>
      task.schedules?.length
        ? getTaskDayCompletionPercentage(task, {
            day: refDate,
            weekStartsOn,
          })
        : undefined,
    [refDate, task, weekStartsOn],
  );

  const target = useMemo(() => {
    const activeSchedule = getActiveSchedule(task.schedules, today);
    if (!activeSchedule) {
      return;
    }

    return getScheduleDayTarget(activeSchedule);
  }, [task.schedules, today]);

  const isRepeatingIcon =
    !!isFutureRepeating ||
    [
      TaskCheckOptions.RepeatSingleChecked,
      TaskCheckOptions.RepeatChecked,
    ].includes(checkOption);

  return (
    <>
      <EventCard
        title={task.name}
        color={color}
        timeText={timeText}
        completed={completed}
        isDeadline={isDeadline || showDeadlineFlag}
        numberOfLines={numberOfLines}
        onClick={_onClick}
        onContextMenu={openMenu}
        trailing={
          <TaskEventSyncBadge
            task={task}
            calendarSync={calendarSync}
            color={color}
          />
        }
        leading={
          isRepeatingIcon ? (
            <Styled.LeadingSlot $color={color} $completed={completed}>
              <Icon
                icon={task.iconName ? IconFormIcons[task.iconName] : Repeat}
                strokeWidth={2}
              />
            </Styled.LeadingSlot>
          ) : (
            <Styled.MetaContainer>
              <TaskDetailCheckbox
                checked={checkOption}
                name={task.name}
                completionPercentage={completionPercentage}
                target={target}
                priority={task.priority}
                onChange={_onComplete}
                color={color}
                isFrozen={!!task.frozenAt}
                isSmall
                isFaded={completed}
              />
            </Styled.MetaContainer>
          )
        }
      />

      {!!menuPosition && (
        <TaskCardContextMenu
          task={task}
          onClose={closeMenu}
          location={menuPosition}
          timeslotDate={refDate}
          occurrenceId={occurrenceId}
          onAction={onInteractionComplete}
        />
      )}
    </>
  );
};
